import React, { Fragment } from 'react';
import {
  Row,
  Column,
  Header,
  Container
} from "@front10/landing-page-book/dist/components";

const Comparison = () => {
  return (
    <Fragment>
      <Container>
        <Row style={{marginTop: '40px', marginBottom: '40px'}}>
          <Column>
            <Header type="h1" style={{fontSize: '48px', fontWeight: '800', marginBottom: '20px', color: '#1a1a1a'}}>
              Decision Tracker vs. Slack Native Features
            </Header>
            <p style={{fontSize: '20px', lineHeight: '1.7', color: '#4a5568', marginBottom: '40px'}}>
              Slack's built-in features (Canvas, Lists, Workflow Builder) are excellent for documentation, tasks, and automation—but they weren't designed for decision management. Decision Tracker fills this gap by adding accountability, approval workflows, and analytics specifically for team decisions.
            </p>
          </Column>
        </Row>

        {/* Comparison Table */}
        <Row style={{marginBottom: '60px'}}>
          <Column>
            <div style={{overflowX: 'auto'}}>
              <table style={{width: '100%', borderCollapse: 'collapse', backgroundColor: '#fff', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: '12px'}}>
                <thead>
                  <tr style={{backgroundColor: '#EF4F4F', color: '#fff'}}>
                    <th style={{padding: '20px', textAlign: 'left', fontWeight: '700', fontSize: '16px'}}>Feature</th>
                    <th style={{padding: '20px', textAlign: 'center', fontWeight: '700', fontSize: '16px'}}>Slack Canvas</th>
                    <th style={{padding: '20px', textAlign: 'center', fontWeight: '700', fontSize: '16px'}}>Slack Lists</th>
                    <th style={{padding: '20px', textAlign: 'center', fontWeight: '700', fontSize: '16px'}}>Workflow Builder</th>
                    <th style={{padding: '20px', textAlign: 'center', fontWeight: '700', fontSize: '16px', backgroundColor: '#d63d3d'}}>Decision Tracker</th>
                  </tr>
                </thead>
                <tbody>
                  <ComparisonRow feature="Track decisions" canvas="✅ Manual" lists="✅ Manual" workflow="❌ No" tracker="✅ One-click" />
                  <ComparisonRow feature="Link conversation context" canvas="❌ Manual links" lists="❌ No" workflow="❌ No" tracker="✅ Automatic" highlight />
                  <ComparisonRow feature="Approval workflows" canvas="❌ No" lists="❌ No" workflow="⚠️ Limited" tracker="✅ Yes" />
                  <ComparisonRow feature="Automated reminders" canvas="❌ No" lists="❌ No" workflow="⚠️ Basic" tracker="✅ Smart nudges" highlight />
                  <ComparisonRow feature="Decision-specific analytics" canvas="❌ No" lists="❌ No" workflow="❌ No" tracker="✅ Yes" />
                  <ComparisonRow feature="Search decision history" canvas="⚠️ Text search" lists="⚠️ Limited" workflow="❌ No" tracker="✅ Advanced" highlight />
                  <ComparisonRow feature="Export to CSV" canvas="❌ No" lists="❌ No" workflow="❌ No" tracker="✅ Yes" />
                  <ComparisonRow feature="Decision status tracking" canvas="❌ Manual" lists="⚠️ Basic" workflow="❌ No" tracker="✅ Automated" highlight />
                  <ComparisonRow feature="Reopen & iterate" canvas="✅ Yes" lists="❌ No" workflow="❌ No" tracker="✅ With history" />
                  <ComparisonRow feature="Filter by owner/date" canvas="❌ No" lists="⚠️ Limited" workflow="❌ No" tracker="✅ Yes" highlight />
                </tbody>
              </table>
            </div>
            <p style={{fontSize: '14px', color: '#718096', marginTop: '16px', textAlign: 'center'}}>
              <strong>Legend:</strong> ✅ Full support • ⚠️ Partial/limited support • ❌ Not supported
            </p>
          </Column>
        </Row>

        {/* Detailed Comparisons */}
        <Row style={{marginBottom: '60px'}}>
          <Column>
            <Header type="h2" style={{fontSize: '36px', fontWeight: '800', marginBottom: '30px', color: '#1a1a1a'}}>
              Detailed Comparison
            </Header>
          </Column>
        </Row>

        {/* Slack Canvas */}
        <Row style={{marginBottom: '50px'}}>
          <Column md={12}>
            <div style={{padding: '40px', backgroundColor: '#f7fafc', borderRadius: '12px', border: '2px solid #e2e8f0'}}>
              <Header type="h3" style={{fontSize: '28px', fontWeight: '700', marginBottom: '16px', color: '#1a1a1a'}}>
                1. Slack Canvas
              </Header>

              <div style={{marginBottom: '24px'}}>
                <Header type="h4" style={{fontSize: '18px', fontWeight: '700', marginBottom: '12px', color: '#2d3748'}}>
                  What it's great for:
                </Header>
                <ul style={{fontSize: '16px', lineHeight: '1.8', color: '#4a5568', paddingLeft: '20px'}}>
                  <li>Documentation</li>
                  <li>Project briefs</li>
                  <li>Meeting notes</li>
                  <li>Collaborative writing</li>
                </ul>
              </div>

              <div>
                <Header type="h4" style={{fontSize: '18px', fontWeight: '700', marginBottom: '12px', color: '#2d3748'}}>
                  What it's NOT great for (where Decision Tracker excels):
                </Header>
                <ul style={{fontSize: '16px', lineHeight: '1.8', color: '#4a5568', paddingLeft: '20px'}}>
                  <li>❌ No automatic conversation linking - You have to manually copy/paste links</li>
                  <li>❌ No approval workflows - Can't request formal approvals with reminders</li>
                  <li>❌ No analytics - Can't see decision velocity or bottlenecks</li>
                  <li>❌ No automated reminders - Won't nudge stakeholders for decisions</li>
                  <li>❌ No structured decision history - Hard to find old decisions quickly</li>
                  <li>❌ No status tracking - Can't filter by "Pending approval" or "Resolved"</li>
                </ul>
              </div>
            </div>
          </Column>
        </Row>

        {/* Slack Lists */}
        <Row style={{marginBottom: '50px'}}>
          <Column md={12}>
            <div style={{padding: '40px', backgroundColor: '#f7fafc', borderRadius: '12px', border: '2px solid #e2e8f0'}}>
              <Header type="h3" style={{fontSize: '28px', fontWeight: '700', marginBottom: '16px', color: '#1a1a1a'}}>
                2. Slack Lists
              </Header>

              <div style={{marginBottom: '24px'}}>
                <Header type="h4" style={{fontSize: '18px', fontWeight: '700', marginBottom: '12px', color: '#2d3748'}}>
                  What it's great for:
                </Header>
                <ul style={{fontSize: '16px', lineHeight: '1.8', color: '#4a5568', paddingLeft: '20px'}}>
                  <li>Todo lists</li>
                  <li>Bug tracking</li>
                  <li>Simple task management</li>
                  <li>Quick item capture</li>
                </ul>
              </div>

              <div>
                <Header type="h4" style={{fontSize: '18px', fontWeight: '700', marginBottom: '12px', color: '#2d3748'}}>
                  What it's NOT great for (where Decision Tracker excels):
                </Header>
                <ul style={{fontSize: '16px', lineHeight: '1.8', color: '#4a5568', paddingLeft: '20px'}}>
                  <li>❌ No conversation context - Can't see the discussion that led to the decision</li>
                  <li>❌ No approval workflows - Can't request stakeholder sign-off</li>
                  <li>❌ Not decision-specific - Designed for tasks, not decisions</li>
                  <li>❌ No analytics - Can't measure decision-making effectiveness</li>
                  <li>❌ No deadline reminders - No automated nudges</li>
                  <li>❌ Limited search - Can't filter by decision criteria</li>
                </ul>
              </div>
            </div>
          </Column>
        </Row>

        {/* Slack Workflow Builder */}
        <Row style={{marginBottom: '50px'}}>
          <Column md={12}>
            <div style={{padding: '40px', backgroundColor: '#f7fafc', borderRadius: '12px', border: '2px solid #e2e8f0'}}>
              <Header type="h3" style={{fontSize: '28px', fontWeight: '700', marginBottom: '16px', color: '#1a1a1a'}}>
                3. Slack Workflow Builder
              </Header>

              <div style={{marginBottom: '24px'}}>
                <Header type="h4" style={{fontSize: '18px', fontWeight: '700', marginBottom: '12px', color: '#2d3748'}}>
                  What it's great for:
                </Header>
                <ul style={{fontSize: '16px', lineHeight: '1.8', color: '#4a5568', paddingLeft: '20px'}}>
                  <li>Automating repetitive processes</li>
                  <li>Simple approval requests</li>
                  <li>Standup reminders</li>
                  <li>Form submissions</li>
                </ul>
              </div>

              <div>
                <Header type="h4" style={{fontSize: '18px', fontWeight: '700', marginBottom: '12px', color: '#2d3748'}}>
                  What it's NOT great for (where Decision Tracker excels):
                </Header>
                <ul style={{fontSize: '16px', lineHeight: '1.8', color: '#4a5568', paddingLeft: '20px'}}>
                  <li>❌ No long-term decision tracking - Workflows execute and disappear</li>
                  <li>❌ No decision history - Can't search past approvals</li>
                  <li>❌ No conversation linking - Can't capture the context around decisions</li>
                  <li>❌ No analytics - Can't see patterns in decision-making</li>
                  <li>❌ Complex to set up - Requires workflow configuration for each decision type</li>
                  <li>❌ Not searchable - Can't find "What did we decide about X last month?"</li>
                </ul>
              </div>
            </div>
          </Column>
        </Row>

        {/* When to Use What */}
        <Row style={{marginBottom: '60px'}}>
          <Column>
            <Header type="h2" style={{fontSize: '36px', fontWeight: '800', marginBottom: '30px', color: '#1a1a1a', textAlign: 'center'}}>
              When to Use What?
            </Header>
          </Column>
        </Row>

        <Row className="home-grid" style={{marginBottom: '60px'}}>
          <Column md={6}>
            <div style={{padding: '32px', borderRadius: '12px', backgroundColor: '#fff', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', height: '100%', marginBottom: '20px'}}>
              <Header type="h4" style={{fontSize: '22px', fontWeight: '700', marginBottom: '16px', color: '#1a1a1a'}}>
                Use Slack Canvas When:
              </Header>
              <ul style={{fontSize: '16px', lineHeight: '1.8', color: '#4a5568', paddingLeft: '20px'}}>
                <li>Writing documentation</li>
                <li>Creating project briefs</li>
                <li>Collaborative writing</li>
                <li>Meeting notes</li>
              </ul>
            </div>
          </Column>

          <Column md={6}>
            <div style={{padding: '32px', borderRadius: '12px', backgroundColor: '#fff', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', height: '100%', marginBottom: '20px'}}>
              <Header type="h4" style={{fontSize: '22px', fontWeight: '700', marginBottom: '16px', color: '#1a1a1a'}}>
                Use Slack Lists When:
              </Header>
              <ul style={{fontSize: '16px', lineHeight: '1.8', color: '#4a5568', paddingLeft: '20px'}}>
                <li>Managing todos</li>
                <li>Tracking bugs</li>
                <li>Simple task management</li>
                <li>Quick captures</li>
              </ul>
            </div>
          </Column>

          <Column md={6}>
            <div style={{padding: '32px', borderRadius: '12px', backgroundColor: '#fff', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', height: '100%', marginBottom: '20px'}}>
              <Header type="h4" style={{fontSize: '22px', fontWeight: '700', marginBottom: '16px', color: '#1a1a1a'}}>
                Use Workflow Builder When:
              </Header>
              <ul style={{fontSize: '16px', lineHeight: '1.8', color: '#4a5568', paddingLeft: '20px'}}>
                <li>Automating repetitive approvals (PTO, expenses)</li>
                <li>Scheduled reminders</li>
                <li>Simple form submissions</li>
                <li>Standup automations</li>
              </ul>
            </div>
          </Column>

          <Column md={6}>
            <div style={{padding: '32px', borderRadius: '12px', backgroundColor: '#EF4F4F', color: '#fff', boxShadow: '0 4px 20px rgba(239,79,79,0.3)', height: '100%', marginBottom: '20px'}}>
              <Header type="h4" style={{fontSize: '22px', fontWeight: '700', marginBottom: '16px', color: '#fff'}}>
                Use Decision Tracker When:
              </Header>
              <ul style={{fontSize: '16px', lineHeight: '1.8', color: '#fff', paddingLeft: '20px'}}>
                <li>Tracking product/feature decisions</li>
                <li>Recording architectural decisions (ADRs)</li>
                <li>Managing approval workflows with accountability</li>
                <li>Needing decision history and context</li>
                <li>Analyzing decision-making effectiveness</li>
                <li>Requiring stakeholder sign-offs</li>
                <li>Exporting decisions for reporting</li>
              </ul>
            </div>
          </Column>
        </Row>

        {/* Integration Approach */}
        <Row style={{marginBottom: '60px'}}>
          <Column>
            <div style={{padding: '50px', backgroundColor: 'linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)', borderRadius: '16px', border: '2px solid #e2e8f0'}}>
              <Header type="h2" style={{fontSize: '32px', fontWeight: '800', marginBottom: '20px', color: '#1a1a1a', textAlign: 'center'}}>
                The Integration Approach
              </Header>
              <p style={{fontSize: '18px', lineHeight: '1.7', color: '#4a5568', textAlign: 'center', marginBottom: '30px', maxWidth: '800px', margin: '0 auto 30px'}}>
                Decision Tracker doesn't replace Slack's native features—it complements them.
              </p>

              <div style={{maxWidth: '700px', margin: '0 auto'}}>
                <ol style={{fontSize: '16px', lineHeight: '2', color: '#4a5568', paddingLeft: '20px'}}>
                  <li><strong>Track a decision</strong> in Decision Tracker</li>
                  <li><strong>Document the details</strong> in a Slack Canvas (link it to the decision)</li>
                  <li><strong>Create action items</strong> in Slack Lists (reference the decision)</li>
                  <li><strong>Automate reminders</strong> with Workflow Builder (for recurring check-ins)</li>
                </ol>
              </div>
            </div>
          </Column>
        </Row>

        {/* ROI Calculation */}
        <Row style={{marginBottom: '60px'}}>
          <Column>
            <Header type="h2" style={{fontSize: '36px', fontWeight: '800', marginBottom: '30px', color: '#1a1a1a', textAlign: 'center'}}>
              ROI Calculation
            </Header>
          </Column>
        </Row>

        <Row style={{marginBottom: '60px'}}>
          <Column md={12}>
            <div style={{padding: '40px', backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)'}}>
              <Header type="h4" style={{fontSize: '22px', fontWeight: '700', marginBottom: '20px', color: '#1a1a1a'}}>
                Example Scenario:
              </Header>

              <div style={{fontSize: '16px', lineHeight: '1.8', color: '#4a5568', marginBottom: '24px'}}>
                <p><strong>Assume:</strong></p>
                <ul style={{paddingLeft: '20px'}}>
                  <li>Team of 10 people making 50 decisions/month</li>
                  <li>Average hourly rate: $50/hour</li>
                  <li>Time saved per decision: 15 minutes (faster approvals, less follow-up)</li>
                </ul>
              </div>

              <div style={{fontSize: '16px', lineHeight: '1.8', color: '#4a5568', marginBottom: '24px'}}>
                <p><strong>Time saved per month:</strong></p>
                <p>50 decisions × 15 minutes = <strong>12.5 hours</strong></p>
              </div>

              <div style={{fontSize: '16px', lineHeight: '1.8', color: '#4a5568', marginBottom: '24px'}}>
                <p><strong>Money saved per month:</strong></p>
                <p>12.5 hours × $50/hour = <strong>$625</strong></p>
              </div>

              <div style={{fontSize: '18px', lineHeight: '1.8', color: '#1a1a1a', padding: '24px', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '2px solid #86efac'}}>
                <p><strong>ROI:</strong></p>
                <p style={{marginBottom: '8px'}}>Cost: <strong>$4.99/month</strong></p>
                <p style={{marginBottom: '8px'}}>Savings: <strong>$625/month</strong></p>
                <p style={{fontSize: '24px', fontWeight: '800', color: '#15803d', marginTop: '16px'}}>ROI: 12,400%</p>
              </div>
            </div>
          </Column>
        </Row>

        {/* CTA Section */}
        <Row style={{marginTop: '80px', marginBottom: '80px'}}>
          <Column className="text-center">
            <div style={{padding: '60px 40px', background: 'linear-gradient(135deg, #EF4F4F 0%, #d63d3d 100%)', borderRadius: '16px'}}>
              <Header type="h2" style={{fontSize: '36px', fontWeight: '800', marginBottom: '20px', color: '#fff'}}>
                Ready to track decisions with full context?
              </Header>
              <p style={{fontSize: '20px', lineHeight: '1.7', color: '#fff', opacity: 0.95, marginBottom: '30px', maxWidth: '700px', margin: '0 auto 30px'}}>
                Start your 7-day free trial. No credit card required.
              </p>
              <a
                href={process.env.REACT_APP_HOSTNAME ? `${process.env.REACT_APP_HOSTNAME}/trial` : '/trial'}
                style={{
                  alignItems:'center',
                  color:'#1a1a1a',
                  backgroundColor:'#fff',
                  border:'none',
                  borderRadius:'8px',
                  display:'inline-flex',
                  fontFamily:'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                  fontSize:'18px',
                  fontWeight:700,
                  height:'64px',
                  justifyContent:'center',
                  textDecoration:'none',
                  width:'auto',
                  padding:'0 40px',
                  boxShadow:'0 4px 14px rgba(0,0,0,0.15)',
                  transition:'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 8px 20px rgba(0,0,0,0.25)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 14px rgba(0,0,0,0.15)';
                }}
              >
                Start Your Free Trial
              </a>
            </div>
          </Column>
        </Row>

      </Container>
    </Fragment>
  );
};

const ComparisonRow = ({ feature, canvas, lists, workflow, tracker, highlight }) => (
  <tr style={{backgroundColor: highlight ? '#f7fafc' : '#fff', borderBottom: '1px solid #e2e8f0'}}>
    <td style={{padding: '16px', fontSize: '15px', fontWeight: '600', color: '#2d3748'}}>{feature}</td>
    <td style={{padding: '16px', fontSize: '14px', color: '#4a5568', textAlign: 'center'}}>{canvas}</td>
    <td style={{padding: '16px', fontSize: '14px', color: '#4a5568', textAlign: 'center'}}>{lists}</td>
    <td style={{padding: '16px', fontSize: '14px', color: '#4a5568', textAlign: 'center'}}>{workflow}</td>
    <td style={{padding: '16px', fontSize: '14px', color: '#1a1a1a', textAlign: 'center', fontWeight: '600', backgroundColor: highlight ? '#fef2f2' : '#fff9f9'}}>{tracker}</td>
  </tr>
);

export default Comparison;



// WEBPACK FOOTER //
// ./src/components/Comparison.js